import {
  Component,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  TemplateRef,
  viewChild,
  ChangeDetectionStrategy,
  computed,
  signal
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  NavigationStart,
  Router,
  RouterModule,
  RouterOutlet
} from '@angular/router';
import { CommonModule } from '@angular/common';
import {
  MatBottomSheet,
  MatBottomSheetModule
} from '@angular/material/bottom-sheet';

import { IconComponent } from '@factor_ec/ui';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { PROFILE_IDS } from '@/core/constants/profileIds';

import { SessionService } from '@/core/session.service';
import { SimulationService } from '@/main/services/simulation.service';
import { environment } from '@/environments/environment';
import { CreditService } from '@/main/services/credit.service';
import { ConnectionService } from '@/main/services/connection.service';
import { LoadingComponent } from '@/shared/loading/loading.component';
import { AppUpdateService } from '@/core/services/app-version.service';
import { UpdateFullscreenComponent } from '@/core/components/update-fullscreen/update-fullscreen/update-fullscreen.component';
import { AppService } from '@/core/app.service';
import { IsGrantedDirective } from '@/core/directives/is-granted.directive';
import { GroupService } from '@/main/services/group.service';
import { LoadingService } from './shared/loading/loading.service';
import { FormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';

/**
 * @description Componente principal de la aplicación MePrestas
 * @class AppComponent
 */
@Component({
  selector: 'app-root',
  imports: [
    IsGrantedDirective,
    IconComponent,
    MatButtonModule,
    MatBottomSheetModule,
    RouterModule,
    RouterOutlet,
    CommonModule,
    UpdateFullscreenComponent,
    LoadingComponent
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  host: {
    class: 'ft-root'
  },
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit, OnDestroy {
  // Injección de dependencias
  private readonly bottomSheet = inject(MatBottomSheet);
  private readonly connectionService = inject(ConnectionService);
  private readonly creditService = inject(CreditService);
  private readonly simulationService = inject(SimulationService);
  private readonly groupService = inject(GroupService);
  public readonly sessionService = inject(SessionService);
  private readonly router = inject(Router);
  private readonly appService = inject(AppService);
  private readonly loadingService = inject(LoadingService);
  private readonly dialog = inject(MatDialog);

  private navSubscription?: Subscription;
  public isBeneficiary = computed<boolean>(() => this.sessionService.params()['profileId'] === PROFILE_IDS.BasicBeneficiary);
  public simulateCreditButtonTitle = computed<string>(() => !this.isBeneficiary() ? $localize`Calculate the installments and interest of a loan. You can save it as an offer.` : $localize`Calculate the installments and interest of a loan.`);
  private readonly profilesTemplate = viewChild<TemplateRef<any>>('profilesTemplate');

  // Propiedades (presentación)
  isAuthenticated$ = this.appService.isAuthenticated$;

  /** Opciones del menú de navegación (navbar) */
  readonly menuItems = computed(() =>
    [
      {
        label: $localize`Payments`,
        icon: 'installment',
        url: '/payments',
        class: '',
        visible: this.sessionService.isGranted('MPRM01')
      },
      {
        label: $localize`Offers`,
        icon: 'offers',
        url: '/simulations',
        class: '',
        visible: this.sessionService.isGranted('MPRM02')
      },
      {
        label: $localize`Loans`,
        icon: 'loans2',
        url: '/credits',
        class: '',
        visible: this.sessionService.isGranted('MPRM03')
      },
      {
        label: $localize`Beneficiaries`,
        icon: 'beneficiary',
        url: '/beneficiaries',
        class: 'ft-scale-12',
        visible: this.sessionService.isGranted('MPRM07')
      },
      {
        label: $localize`Groups`,
        icon: 'group',
        url: '/groups',
        class: 'ft-scale-12',
        visible: this.sessionService.isGranted('MPRM06')
      },
      {
        label: $localize`Connections`,
        icon: 'connection',
        url: '/inversors',
        class: 'ft-scale-12',
        visible: this.sessionService.isGranted('MPRM08')
      },
      {
        label: $localize`Help`,
        icon: 'help',
        type: 'external',
        href: this.urls.help,
        visible: true
      },
      {
        label: $localize`Configuration`,
        icon: 'settings',
        url: '/settings',
        visible: this.sessionService.isGranted('MPRM09')
      }
    ].filter(a => a.visible)
  );

  private readonly actionsTemplate =
    viewChild.required<TemplateRef<any>>('actionsTemplate');

  private readonly optionsTemplate =
    viewChild.required<TemplateRef<any>>('optionsTemplate');

  /** @description Título de la aplicación */
  title = 'MePrestas';

  public urls = {
    appUsagePolicy: environment.appUsagePolicyUrl,
    dataProtectionPolicy: environment.dataProtectionPolicyUrl,
    help: environment.helpUrl,
    aboutUs: environment.aboutUsUrl
  };

  public profile = computed(() => {
    return this.sessionService
      .settings()
      ?.profiles.find(
        profile => profile.id === this.sessionService.params()['profileId']
      );
  });

  /**
   * @description Previene el menú contextual del click derecho
   * @param {MouseEvent} event - Evento del mouse
   */
  @HostListener('document:contextmenu', ['$event'])
  onRightClick(event: MouseEvent) {
    event.preventDefault();
  }

  ngOnInit(): void {
    this.navSubscription = this.router.events
      .pipe(
        filter(
          (event): event is NavigationStart => event instanceof NavigationStart
        )
      )
      .subscribe(() => this.bottomSheet.dismiss());
  }

  ngOnDestroy(): void {
    this.navSubscription?.unsubscribe();
  }

  openActions(): void {
    this.bottomSheet.open(this.actionsTemplate());
  }
  openOptions(): void {
    this.bottomSheet.open(this.optionsTemplate());
  }
  openForm(type: 'simulation' | 'credit' | 'beneficiary' | 'group'): void {
    switch (type) {
      case 'simulation':
        this.simulationService.open();
        break;
      case 'credit':
        this.creditService.create();
        break;
      case 'beneficiary':
        this.connectionService.openBeneficiary();
        break;
      case 'group':
        this.groupService.open();
        break;
    }
    this.bottomSheet.dismiss();
  }
  public openChangeProfile(): void {
    const template = this.profilesTemplate();
    if (template) {
      this.dialog.open(template, {
        panelClass: ['ft-dialog'],
        width: '300px',
        autoFocus: false
      });
    }
  }

  public async changeProfile(profileId: string): Promise<void> {
    this.dialog.closeAll();
    try {
      this.loadingService.show();
      this.sessionService.setParam('profileId', profileId);
      await this.sessionService.getSettingsAuth0(
        this.sessionService.settings()?.email
      );

      const route = profileId === PROFILE_IDS.BasicBeneficiary
        ? '/credits'
        : '/payments';

      await this.router.navigate([route]);

    } finally {
      this.loadingService.hide();
    }
  }
}
